import {toTwoDecimals} from "./picos/numberFormat";
import {Err, Ok, Result} from "oxide.ts";
import sleep from "./picos/sleep";
import {HttpError} from "./picos/http";

export type FetchFileCallback = (progress: number) => void;

/**
 *
 * @param {string} url - the url to fetch
 * @param {boolean} cors - use cors in fetch?
 * @param {FetchFileCallback} [cb] - an optional callback function to pass read %
 * @returns {Promise<string|any>} - the file that was read as string or a json object/array
 */
async function fetchFileWithProgress(url: string, cors: boolean, cb?: FetchFileCallback, signal?: AbortSignal): Promise<string | any> {
	// Step 1: start the fetch and obtain a reader
	let response: Response, reader: ReadableStreamDefaultReader<Uint8Array>;

	// Always a GET
	const options = {
		method: 'GET',
		mode: null,
		signal,
	}

	// Apply CORS options to the call
	options.mode = cors ? 'cors' : 'no-cors';

	// Get the file
	response = await fetch(url, options);
	signal?.throwIfAborted();

	// If we did not get a valid response, throw an error
	if (!response.ok) throw Error(`Fetch failed. ${response.status} - ${response.statusText}`);

	// If the response says that it is using the body, but there isn't a body, throw an error
	if (response.bodyUsed && !response.body) throw ('No response body from ' + url);

	const isJSON = response.headers.get('content-type')?.includes('application/json');
	reader = response.body.getReader();

	// Step 2: get total length
	const contentLength = parseInt(response.headers.get('Content-Length') || '0');

	// Step 3: read the data
	let receivedLength = 0; // received that many bytes at the moment
	let chunks = []; // array of received binary chunks (comprises the body)

	while (true) {
		const {done, value} = await reader.read();
		signal?.throwIfAborted();

		if (done) {
			break;
		}

		chunks.push(value);
		receivedLength += value.length;

		if (typeof (cb) === 'function') {
			cb(toTwoDecimals(receivedLength / contentLength));
			await new Promise(resolve => setTimeout(resolve, 1)); // yield for display
		}
	}

	if (typeof (cb) === 'function') {
		signal?.throwIfAborted();
		cb(1);
		await new Promise(resolve => setTimeout(resolve, 1)); // yield for display
	}

	// Step 4: concatenate chunks into single Uint8Array
	let allChunks = new Uint8Array(receivedLength); // (4.1)

	let position = 0;
	for (let chunk of chunks) {
		allChunks.set(chunk, position); // (4.2)
		position += chunk.length;
	}

	// Step 5: decode into a string
	const rawText = new TextDecoder("utf-8").decode(allChunks);
	signal?.throwIfAborted();

	if (isJSON){
		try {
			return JSON.parse(rawText);
		}
		catch (err) {
			console.error('fetchFileWithProgress: error parsing JSON', err.message);
            return rawText;
        }
	}
	else return rawText;
}

/**
 *
 * @param {string} url - the url to fetch
 * @param {boolean} cors - use cors in fetch?
 * @param {FetchFileCallback} [cb] - an optional callback function to pass read %
 * @returns {Promise<string|any>} - the file that was read as string or a json object/array
 */
export async function fetchFileWithProgressResult(url: string, cors: boolean, cb?: FetchFileCallback): Promise<Result<string, HttpError>> {
	// Step 1: start the fetch and obtain a reader
	let response: Response;
	let reader: ReadableStreamDefaultReader<Uint8Array>;

	// Always a GET
	const options = {
		method: 'GET',
		mode: null
	}

	// Apply CORS options to the call
	options.mode = cors ? 'cors' : 'no-cors';

	// Get the file
	response = await fetch(url, options);

	// If we did not get a valid response, throw an error
	if (!response.ok) return Err(new HttpError(response.status, response.statusText || `Request failed (HTTP ${response.status})`));

	// If the response says that it is using the body, but there isn't a body, throw an error
	if (response.bodyUsed && !response.body) return Err(new HttpError(response.status, 'No response body from' + url));

	const isJSON = response.headers.get('content-type')?.includes('application/json');
	reader = response.body.getReader();

	// Step 2: get total length
	const contentLength = parseInt(response.headers.get('Content-Length') || '0');

	// Step 3: read the data
	let receivedLength = 0; // received that many bytes at the moment
	let chunks = []; // array of received binary chunks (comprises the body)

	while (true) {
		const {done, value} = await reader.read();

		if (done) {
			break;
		}

		chunks.push(value);
		receivedLength += value.length;

		if (typeof (cb) === 'function') {
			cb(toTwoDecimals(receivedLength / contentLength));
			await sleep(1); // yield for display
		}
	}

	if (typeof (cb) === 'function') {
		cb(1);
		await sleep(1);
	}

	// Step 4: concatenate chunks into single Uint8Array
	let allChunks = new Uint8Array(receivedLength); // (4.1)

	let position = 0;
	for (let chunk of chunks) {
		allChunks.set(chunk, position); // (4.2)
		position += chunk.length;
	}

	// Step 5: decode into a string
	const rawText = new TextDecoder("utf-8").decode(allChunks);

	if (isJSON){
		try {
			return Ok(JSON.parse(rawText));
		}
		catch (err) {
			console.error('fetchFileWithProgress: error parsing JSON', err.message);
			return Ok(rawText);
		}
	}
	else return Ok(rawText);
}

export default fetchFileWithProgress;
